import getSymbolFromCurrency from "currency-symbol-map";

import { AudienceDto } from "@intelligems/ig-types";
import { isAnyOf } from "@reduxjs/toolkit";
import type { AppListenerMiddleware } from "@src/store/rootState";
import {
  clearAudience,
  deleteFilterRow,
  selectedAudience,
  setAudience,
  setAudienceCurrencyDefault,
  setAudienceCurrencyValue,
  setAudienceElse,
  setAudienceExpression,
  updateAudienceRow,
} from "@src/store/slices/audiences";
import {
  selectedExperience,
  setExperienceCurrency,
} from "@src/store/slices/experiences";
import {
  getCheckoutBlocks,
  initNewPostPurchaseBlock,
  setCheckoutBlockVariable,
} from "@src/store/slices/checkoutBlocks";
import { getPostPurchaseBlocks } from "@src/store/slices/checkoutBlocks/selectors";
import { getCurrencyConfiguration } from "@src/store/slices/organization/selectors/getCurrencyConfiguration";
import { isPriceTest } from "@src/store/slices/experiences/selectors/price/isPriceTest";
import { getControlGroup } from "@src/store/slices/variations";

export function setupAudiencesListener(middleware: AppListenerMiddleware) {
  middleware.startListening({
    matcher: isAnyOf(
      setAudience,
      clearAudience,
      deleteFilterRow,
      updateAudienceRow,
      setAudienceExpression
    ),
    effect: (_, { getState, dispatch }) => {
      const audience = selectedAudience(getState());
      const priceTest = isPriceTest(getState());
      const controlGroup = getControlGroup(getState());
      const update = createUpdate(dispatch, audience);
      const hasFilters = Boolean(audience.filters?.length);
      if (!hasFilters) {
        update({
          elseAction: null,
          elseVariationId: null,
          elseExcludeFromAnalytics: false,
        });
      } else if (audience.elseAction === null) {
        if (priceTest && controlGroup) {
          update({
            elseAction: "assignVariation",
            elseVariationId: controlGroup.id,
            elseExcludeFromAnalytics: true,
          });
        } else {
          update({
            elseAction: "experienceUnassigned",
            elseVariationId: null,
            elseExcludeFromAnalytics: false,
          });
        }
      }
    },
  });
  middleware.startListening({
    matcher: isAnyOf(initNewPostPurchaseBlock, setAudienceCurrencyValue),
    effect: (_, { getState, dispatch }) => {
      const state = getState();
      const isPostPurchase =
        selectedExperience(state)?.type === "postPurchase" ||
        getPostPurchaseBlocks(state).length > 0;
      if (!isPostPurchase) {
        return;
      }

      const storeCurrency = getCurrencyConfiguration(state)?.code ?? "USD";
      if (
        selectedAudience(state)?.excludeCurrency?.currency !== storeCurrency
      ) {
        dispatch(setAudienceCurrencyDefault(storeCurrency));
      }

      // A shipping progress bar block has its own currency variable.
      // It should also match the store default when a PPU is present.
      let didResetSpb = false;
      getCheckoutBlocks(state).forEach((block) => {
        if (
          block.blockType === "shippingProgressBar" &&
          block.variables.currency !== storeCurrency
        ) {
          dispatch(
            setCheckoutBlockVariable({
              checkoutBlockId: block.id,
              path: "currency",
              value: storeCurrency,
            })
          );
          didResetSpb = true;
        }
      });
      // Keep experience.currency in sync with the block + audience currency
      if (didResetSpb) {
        dispatch(
          setExperienceCurrency({
            code: storeCurrency,
            symbol: getSymbolFromCurrency(storeCurrency) || "$",
          })
        );
      }
    },
  });
}

type SetAudienceElseDispatch = (
  action: ReturnType<typeof setAudienceElse>
) => unknown;

function createUpdate(
  dispatch: SetAudienceElseDispatch,
  audience: AudienceDto
) {
  return (payload: Required<Parameters<typeof setAudienceElse>[0]>) => {
    if (
      audience.elseAction !== payload.elseAction ||
      audience.elseVariationId !== payload.elseVariationId ||
      audience.elseExcludeFromAnalytics !== payload.elseExcludeFromAnalytics
    ) {
      dispatch(setAudienceElse(payload));
    }
  };
}
